import toast from 'react-hot-toast';
import { isRequestError } from 'utils/requestError';
import { saveListExcel } from 'utils/tableExport/saveListExcel';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Card,
  InputDateTime,
  InputSelect,
  InputText,
  Loading,
  TableListData,
} from 'components';
import { TransactionStatusEnum } from 'configs/constants';
import { useLocation } from 'react-router-dom';
import {
  Button,
  IUseFormStructureItem,
  InputWraper,
  NumberUtils,
  useForm,
} from 'modules';
import React, {
  ReactInstance,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  useApproveCancelTransactionMutation,
  useGetElectricTransactionsExportQuery,
  useGetElectricTransactionsQuery,
  useRequestCancelTransactionMutation,
} from 'services';
import type { GetListTransPayEntity, TransPayEntity } from 'services/openapi';
import * as Yup from 'yup';
import {
  columnsDefined,
  convertSelectLeaderOptions,
  convertSelectStatusOptions,
  convertSelectTransactionTypeOptions,
  convertVariables,
  type FilterType,
  getInitFilterValues,
} from './config';
import { useReactToPrint } from 'utils/hooks';
import {
  ModalConfirmElement,
  SelectedA4Voices,
  SelectedThernalVoices,
} from '../Components';
import { Roles } from 'configs/roles';
import { checkValidRole } from 'utils/helpers';

const TransactionsFeature = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const initFilterValues = getInitFilterValues(urlParams);

  const isAdmin = checkValidRole([Roles.SUPER_ADMIN]);

  const printA4Ref = useRef<ReactInstance>(null);

  const printThernalRef = useRef<ReactInstance>(null);

  const [variables, setVariables] = useState<FilterType>(initFilterValues);

  const [refetchDataCurrentPage, setRefetchDataCurrentPage] = useState(0);

  const [dataTable, setDataTable] = useState([]);

  const [dataSelected, setDataSelected] = useState<TransPayEntity[]>([]);

  const [transactionCode, setTransactionCode] = useState<string>('');
  const [transactionCodeRefreshStatus, setTransactionCodeRefreshStatus] =
    useState<string>('');

  const [isOpen, setIsOpen] = useState<boolean>(!!transactionCode);

  const { data: leaders, isLoading } = { data: { data: [] }, isLoading: false };

  const {
    mutateAsync: mutateRefreshStatusTransactionAsync,
    isLoading: isLoadingRefreshStatusCallback,
    data: dataRefreshStatusCallback,
  } = useRequestCancelTransactionMutation();

  const { mutateAsync: mutateRequestCancelTransactionAsync } =
    useRequestCancelTransactionMutation();

  const { mutateAsync: mutateApproveCancelTransactionAsync } =
    useApproveCancelTransactionMutation();

  const handleFilter = useCallback(
    (e: FilterType) => {
      setVariables({
        ...variables,
        ...e,
        // refreshData: variables.refreshData + 1,
      });
    },
    [variables],
  );

  useEffect(() => {
    setIsOpen(!!transactionCode);
  }, [transactionCode]);

  useEffect(() => {
    try {
      if (!transactionCodeRefreshStatus) return;
      handleRefreshStatusTransaction();
    } catch (_) {}
  }, [transactionCodeRefreshStatus]);

  const handleRefreshStatusTransaction = async () => {
    try {
      await mutateRefreshStatusTransactionAsync({
        transactionCode: String(transactionCodeRefreshStatus),
      });
      setTransactionCodeRefreshStatus('');
    } catch (_) {
      setTransactionCodeRefreshStatus('');
    }
  };

  const { isFetching: isLoadingExport, refetch } =
    useGetElectricTransactionsExportQuery({
      ...convertVariables(variables),
      page: '1',
      pageSize: String((dataTable as unknown as GetListTransPayEntity)?.total),
    });

  const filterOptions: IUseFormStructureItem[] = [
    {
      name: 'transactionCode',
      label: 'Mã giao dịch',
      defaultValue: initFilterValues.transactionCode,
    },
    {
      name: 'orderCode',
      label: 'Mã đơn hàng',
      defaultValue: variables.orderCode,
    },
    {
      name: 'status',
      label: 'Trạng thái',
      defaultValue: initFilterValues.status,
      validate: Yup.string().required('Vui lòng chọn Trạng thái'),
    },
    {
      name: 'leader',
      label: 'Trưởng nhóm',
      defaultValue: initFilterValues.leader,
    },
    {
      name: 'userExec',
      label: 'Người thực hiện',
      defaultValue: initFilterValues.userExec,
    },
    {
      name: 'serviceType',
      label: 'Loại dịch vụ',
      defaultValue: initFilterValues.serviceType,
    },
    {
      name: 'fromDate',
      label: 'Ngày & giờ bắt đầu',
      defaultValue: variables.fromDate,
      validate: Yup.string()
        .required('Vui lòng chọn Ngày & giờ bắt đầu')
        .test(
          'comp-fromdate-todate',
          'Ngày & giờ bắt đầu phải nhỏ hơn Ngày & giờ kết thúc',
          function (value) {
            if (value)
              return (
                new Date(value).getTime() <
                new Date(this.parent?.toDate).getTime()
              );
            return false;
          },
        )
        .test(
          'comp-fromdate-todate-60-days',
          'Khoảng cách Ngày & giờ bắt đầu và Ngày & giờ kết thúc không vượt quá 60 ngày',
          function (value) {
            if (value) {
              const dateEnd: Date = new Date(this.parent?.toDate);
              dateEnd.setHours(0, 0, 0, 0);
              dateEnd.setDate(dateEnd.getDate() - 60);
              const dateStart: Date = new Date(value);
              dateEnd.setHours(0, 0, 0, 0);
              return dateEnd.getTime() <= dateStart.getTime();
            }
            return false;
          },
        ),
    },
    {
      name: 'toDate',
      label: 'Ngày & giờ kết thúc',
      defaultValue: variables.toDate,
      validate: Yup.string()
        .required('Vui lòng chọn Ngày & giờ kết thúc')
        .test(
          'comp-fromdate-todate',
          'Ngày & giờ kết thúc phải lớn hơn Ngày & giờ bắt đầu',
          function (value) {
            if (value)
              return (
                new Date(this.parent?.fromDate).getTime() <
                new Date(value).getTime()
              );
            return false;
          },
        )
        .test(
          'comp-fromdate-todate-60-days',
          'Khoảng cách Ngày & giờ bắt đầu và Ngày & giờ kết thúc không vượt quá 60 ngày',
          function (value) {
            if (value) {
              const dateEnd: Date = new Date(value);
              dateEnd.setHours(0, 0, 0, 0);
              dateEnd.setDate(dateEnd.getDate() - 60);
              const dateStart: Date = new Date(this.parent?.fromDate);
              dateEnd.setHours(0, 0, 0, 0);
              return dateEnd.getTime() <= dateStart.getTime();
            }
            return false;
          },
        ),
    },
  ];

  const { getInputProps, handleSubmit } = useForm({
    structure: filterOptions,
    onSubmit: handleFilter,
  });

  const handleExportExcel = async () => {
    if (isLoadingExport) return;
    try {
      const allData = await refetch({ throwOnError: true });
      const summary = allData.data as any;
      await saveListExcel(
        allData.data?.data,
        columnsDefined(
          setTransactionCode,
          setTransactionCodeRefreshStatus,
          isLoadingRefreshStatusCallback,
        ) as ColumnDef<TransPayEntity>[],
        'Danh_sach_giao_dich_thanh_toan_dien',
        true,
        {
          totals: {
            period: 'TỔNG',
            amount: NumberUtils.toFormatNumber(Number(summary.sumAmount ?? 0)),
            paymentAmount: NumberUtils.toFormatNumber(
              Number(summary.sumPayAmount ?? 0),
            ),
            rollbackAmount: NumberUtils.toFormatNumber(
              Number(summary.sumRollBackAmount ?? 0),
            ),
          },
        },
      );
    } catch (error) {
      if (!isRequestError(error))
        toast.error(
          error instanceof Error ? error.message : 'Không thể tải danh sách',
        );
    }
  };

  const handleRequestCancelTransaction = async () => {
    try {
      await mutateRequestCancelTransactionAsync({
        transactionCode: String(transactionCode),
      });
      // reload data
      setTransactionCode('');
      setRefetchDataCurrentPage((prev) => prev + 1);
    } catch (_) {
      setTransactionCode('');
    }
  };

  const handleApproveCancelTransaction = async () => {
    try {
      await mutateApproveCancelTransactionAsync({
        transactionCode: String(transactionCode),
      });
      // reload data
      setTransactionCode('');
      setRefetchDataCurrentPage((prev) => prev + 1);
    } catch (_) {
      setTransactionCode('');
    }
  };

  const handleOnClickCancelTransaction = async () => {
    setIsOpen(false);

    const transactionStatus = (
      dataTable as unknown as GetListTransPayEntity
    )?.data?.find((trans) => trans.transactionCode == transactionCode)?.status;
    isAdmin && transactionStatus == TransactionStatusEnum['Đang chờ hủy']
      ? await handleApproveCancelTransaction()
      : await handleRequestCancelTransaction();
  };

  const elementCancelTransaction = () => {
    const transactionStatus = (
      dataTable as unknown as GetListTransPayEntity
    )?.data?.find((trans) => trans.transactionCode == transactionCode)?.status;
    if (isAdmin && transactionStatus == TransactionStatusEnum['Đang chờ hủy']) {
      return (
        <p>
          Xác nhận hủy giao dịch{' '}
          <span style={{ color: 'red' }}>{String(transactionCode)}</span> ?
        </p>
      );
    } else {
      return (
        <p>
          Bạn muốn gửi yêu cầu hủy giao dịch{' '}
          <span style={{ color: 'red' }}>{String(transactionCode)}</span> ?
        </p>
      );
    }
  };

  const titleCancelTransaction = () => {
    const transactionStatus = (
      dataTable as unknown as GetListTransPayEntity
    )?.data?.find((trans) => trans.transactionCode == transactionCode)?.status;
    if (isAdmin && transactionStatus == TransactionStatusEnum['Đang chờ hủy']) {
      return 'Xác nhận hủy giao dịch';
    } else {
      return 'Gửi yêu cầu hủy';
    }
  };

  const labelApproveCancelTransaction = () => {
    const transactionStatus = (
      dataTable as unknown as GetListTransPayEntity
    )?.data?.find((trans) => trans.transactionCode == transactionCode)?.status;
    if (isAdmin && transactionStatus == TransactionStatusEnum['Đang chờ hủy']) {
      return 'Xác nhận hủy';
    } else {
      return 'Gửi yêu cầu hủy';
    }
  };

  const handlePrintWebA4Invoice = useReactToPrint({
    content: () => printA4Ref?.current,
    copyStyles: true,
    removeAfterPrint: true,
    pageStyle: `
    @page {
        size: A4;
        margin: 40pt 15pt;
    }
    `,
    documentTitle: '',
    suppressErrors: true,
  });

  const handlePrintWebThernalInvoice = useReactToPrint({
    content: () => printThernalRef?.current,
    copyStyles: true,
    removeAfterPrint: true,
    pageStyle: `
    @page {
        size: 225px 690px;
    }
    @page {
      size: auto;
    }
    .print-thermal-content {
      width: 225px;
      height: 690px;
      page-break-after: always;
      margin-top: 0;
      margin-right: 1rem;
      margin-bottom: 0;
      margin-left: 0;
      min-height: auto;
      content: ".";
    }
    `,
    documentTitle: '',
    suppressErrors: true,
  });

  return (
    <>
      <Card className='container-fluid mb20'>
        <form onSubmit={handleSubmit}>
          <div className='row'>
            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('transactionCode')}
                renderInput={InputText}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('orderCode')}
                renderInput={InputText}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('status')}
                isRequired
                renderInput={(props) => (
                  <InputSelect
                    {...props}
                    options={convertSelectStatusOptions()}
                  />
                )}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <Loading isLoading={isLoading}>
                <InputWraper
                  inputProps={getInputProps('leader')}
                  renderInput={(props) => (
                    <InputSelect
                      {...props}
                      options={convertSelectLeaderOptions(
                        (leaders?.data as any[]) || [],
                      )}
                    />
                  )}
                />
              </Loading>
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('userExec')}
                renderInput={InputText}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('serviceType')}
                renderInput={(props) => (
                  <InputSelect
                    {...props}
                    options={convertSelectTransactionTypeOptions()}
                  />
                )}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('fromDate')}
                isRequired
                renderInput={InputDateTime}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('toDate')}
                isRequired
                renderInput={InputDateTime}
              />
            </div>
          </div>

          <div className='textCenter mt16'>
            <Button label={'Tìm kiếm'} type='submit' buttonType='success' />

            <Button
              label={'Tải về DS'}
              onClick={handleExportExcel}
              type='button'
              buttonType='info'
              className='ml10'
              isLoading={isLoadingExport}
            />
          </div>
        </form>
      </Card>

      <TableListData<TransPayEntity>
        columns={
          columnsDefined(
            setTransactionCode,
            setTransactionCodeRefreshStatus,
            isLoadingRefreshStatusCallback,
          ) as ColumnDef<TransPayEntity>[]
        }
        useQuery={useGetElectricTransactionsQuery}
        variables={variables}
        refetchDataCurrentPage={refetchDataCurrentPage}
        setVariables={setVariables}
        convertVariables={convertVariables}
        filterKey='searchDetailContent'
        callBackData={setDataTable}
        onSelectedRowsCallback={setDataSelected}
        canSelectable={(data) => data?.status === 'Thành công'}
        customHeaderBtnGroup={() => (
          <div className='row justify-content-end align-items-center ml5'>
            <Button
              label='In A4'
              onClick={handlePrintWebA4Invoice}
              disabled={!dataSelected?.length}
              type='button'
              buttonType='success-outline'
              className='ml5 mr5'
            />
            <Button
              label='In Nhiệt'
              onClick={handlePrintWebThernalInvoice}
              disabled={!dataSelected?.length}
              type='button'
              buttonType='success-outline'
              className='ml5 mr5'
            />
          </div>
        )}
        customTopRow={(data: any) => {
          if (data) {
            return (
              <>
                <tr>
                  <td colSpan={9}></td>
                  <td>
                    <strong>TỔNG</strong>
                  </td>
                  <td>
                    <strong>
                      {NumberUtils.toFormatNumber(Number(data?.sumAmount ?? 0))}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {NumberUtils.toFormatNumber(
                        Number(data?.sumPayAmount ?? 0),
                      )}
                    </strong>
                  </td>
                  <td>
                    <strong>
                      {NumberUtils.toFormatNumber(
                        Number(data?.sumRollBackAmount ?? 0),
                      )}
                    </strong>
                  </td>
                  <td></td>
                  <td></td>
                </tr>
              </>
            );
          }
        }}
        selectable
        orderColumn
      />

      <div style={{ display: 'none' }}>
        <SelectedA4Voices ref={printA4Ref} selectedInvoices={dataSelected} />
        <SelectedThernalVoices
          ref={printThernalRef}
          selectedInvoices={dataSelected}
        />
      </div>

      <ModalConfirmElement
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setTransactionCode('');
        }}
        onSuccess={handleOnClickCancelTransaction}
        title={titleCancelTransaction()}
        labelApprove={labelApproveCancelTransaction()}
      >
        {elementCancelTransaction()}
      </ModalConfirmElement>
    </>
  );
};

export default TransactionsFeature;
